import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {HighImpactChange, MigrationError} from 'config-migration/MigrationConstants';
import {PrimaryObjectPlan} from 'config-migration/types/MigrationObjects';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {ObjectActionType} from '../../types/objectActions';
import BulkObjectActionDropdown from '../BulkObjectActionDropdown';
import ImportPlanSectionRow from './ImportPlanSectionRow';

export interface ImportPlanSectionProps {
  sectionDisplayName: string;
  plans: Array<PrimaryObjectPlan>;
  onUpdatePlans: (MigrationObjectPlan: Array<PrimaryObjectPlan>) => void;
  collapsed: boolean;
  onUpdateCollapsed: (collapsed: boolean) => void;
  errorsByObjectId: {[objectId: string]: Array<MigrationError>};
  highImpactChangeMap: Map<string, Array<HighImpactChange>>;
}

export function getSummary(objects: Array<PrimaryObjectPlan>): string {
  // eslint-disable-next-line benchlint/no-spread-reduce
  const counts = objects.reduce(
    (actionCounts, object) => ({
      ...actionCounts,
      [object.selectedAction.type]: actionCounts[object.selectedAction.type] + 1,
    }),
    {
      [ObjectActionType.CREATE]: 0,
      [ObjectActionType.UPDATE]: 0,
      [ObjectActionType.IGNORE]: 0,
      [ObjectActionType.KEEP]: 0,
    }
  );

  const summaries = [];
  if (counts[ObjectActionType.CREATE]) {
    summaries.push(`${counts[ObjectActionType.CREATE]} to create`);
  }
  if (counts[ObjectActionType.UPDATE]) {
    summaries.push(`${counts[ObjectActionType.UPDATE]} to update`);
  }
  if (counts[ObjectActionType.IGNORE]) {
    summaries.push(`${counts[ObjectActionType.IGNORE]} ignored`);
  }
  return summaries.join(', ');
}

export function ImportPlanSummary(props: {
  plans: Array<PrimaryObjectPlan>;
  onUpdateCollapsed: (collapsed: boolean) => void;
  collapsed: boolean;
}): JSX.Element {
  return (
    <div
      className={css(styles.summarySection)}
      onClick={() => {
        props.onUpdateCollapsed(!props.collapsed);
      }}
    >
      {getSummary(props.plans)}
      <BenchlingIcon
        icon={props.collapsed ? 'chevron-down' : 'chevron-up'}
        className="u-faded u-mini-spacing-left u-pointer"
      />
    </div>
  );
}

export function ImportPlanSectionWrapper(props: {
  sectionDisplayName: string;
  plans: Array<PrimaryObjectPlan>;
  plansToSummarize?: Array<PrimaryObjectPlan>;
  onUpdatePlans: (MigrationObjectPlan: Array<PrimaryObjectPlan>) => void;
  collapsed: boolean;
  onUpdateCollapsed: (collapsed: boolean) => void;
  children: JSX.Element;
}): JSX.Element {
  return (
    <div className="simpleDivTable u-spacing-top">
      <div className="simpleDivTable-header u-vertical-center-items">
        <h4 className={classNames(css(styles.firstCell), 'simpleDivTable-headerCell')}>
          {props.sectionDisplayName}
        </h4>
        <div className={classNames('simpleDivTable-headerCell', 'u-lr-split', css(styles.headerActions))}>
          <BulkObjectActionDropdown
            className={classNames(props.collapsed && 'invisible')}
            onBulkIgnore={() => {
              props.onUpdatePlans(
                props.plans.map((object) => ({...object, selectedAction: {type: ObjectActionType.IGNORE}}))
              );
            }}
            onBulkReset={() => {
              props.onUpdatePlans(
                props.plans.map((object) => ({...object, selectedAction: object.initialAction}))
              );
            }}
          />
          <ImportPlanSummary
            plans={props.plansToSummarize ?? props.plans}
            onUpdateCollapsed={props.onUpdateCollapsed}
            collapsed={props.collapsed}
          />
        </div>
      </div>
      {!props.collapsed && props.children}
    </div>
  );
}

export default function ImportPlanSection(props: ImportPlanSectionProps): JSX.Element {
  return (
    <ImportPlanSectionWrapper {...props}>
      <>
        {props.plans.map((plan) => (
          <ImportPlanSectionRow
            key={plan.objectId}
            plan={plan}
            errors={props.errorsByObjectId[plan.objectId] || []}
            onUpdatePlan={(updatedPlan) => {
              props.onUpdatePlans([updatedPlan]);
            }}
            highImpactChangeMap={props.highImpactChangeMap}
            nestedDepth={0}
          />
        ))}
      </>
    </ImportPlanSectionWrapper>
  );
}

const styles = StyleSheet.create({
  firstCell: {
    marginLeft: 12,
  },
  summarySection: {
    cursor: 'pointer',
  },
  headerActions: {
    display: 'inline-flex',
  },
});
